import * as Sentry from '@sentry/vue'
import store from '@/vuex/store'
import util from '@/util'
import router from '@/router'
import {
  ROUTE_NAMES,
} from '@/constant/routes'

// The add-on runs in a sandboxed Apps Script iframe, so a google.script.run failure is
// otherwise invisible: it never reaches @sentry/vue's global handler (it is caught, not
// thrown) and the error object is dropped. Report it explicitly at each failure handler.
// ScriptError is a plain object, not an Error, so capturing it directly groups every
// add-on failure under one "Object captured as exception" — wrap it to keep attribution.
const reportAddonFailure = (error, operation, level = 'error') => {
  const detail = error && error.message ? `: ${error.message}` : ''
  Sentry.captureException(new Error(`Google Slides add-on: ${operation} failed${detail}`), {
    level,
    tags: { feature: 'google-slides-addon' },
    extra: { operation, error },
  })
}

const updateQueue = []

let isProcessingQueue = false
const slideId = util.randomIntFromInterval(1, 10000)

const getPresentationGoogleSlide = () => {
  return new Promise((resolve) => {
    if (window.google) {
      window.google.script.run
        .withSuccessHandler(async (info) => {
          resolve(info)
        })
        .withFailureHandler((error) => {
          reportAddonFailure(error, 'getPresentationInfo')
          resolve(null)
          router.push({
            name: ROUTE_NAMES.PRESENTATION_ERROR_GOOGLE_SLIDE,
          })
          const trackData = {
            eventCategory: 'Google Slide Addon',
            eventAction: 'Get Error Aultiple account',
          }
          util.trackGA4AndMixpanel('Google Slide Addon', trackData)
        })
        .getPresentationInfo()
    } else {
      resolve({
        id: `googlePresentationId-${store.state.currentUser.id || 100}`,
        googleSlideId: `googleSlideId-${slideId}`,
        slides: [],
      })
    }
  })
}

// getSlideActive is polled every 300ms by listenGoogleSlideChangeActive, so a
// persistent add-on failure would otherwise flood Sentry ~3x/second — report
// only the first failure of a run and reset once the add-on recovers.
let hasReportedSlideActiveFailure = false

const getGoogleSlideActiveId = () => {
  return new Promise((resolve) => {
    if (window.google) {
      window.google.script.run
        .withSuccessHandler(async (info) => {
          hasReportedSlideActiveFailure = false
          resolve(info)
        })
        .withFailureHandler((error) => {
          if (!hasReportedSlideActiveFailure) {
            hasReportedSlideActiveFailure = true
            reportAddonFailure(error, 'getSlideActive', 'warning')
          }
          resolve(null)
        })
        .getSlideActive()
    } else {
      resolve(`googleSlideId-${slideId}`)
    }
  })
}

const createNewSlideAndSelect = (item, linkPresenting) => {
  return new Promise((resolve) => {
    if (window.google) {
      window.google.script.run
        .withSuccessHandler(async (info) => {
          resolve(info)
        })
        .withFailureHandler((error) => {
          reportAddonFailure(error, 'createNewSlideAndSelect')
          resolve(null)
        })
        .createNewSlideAndSelect(item, linkPresenting)
    } else {
      let idSlide = slideId
      if (item.type === 'leaderboard') {
        idSlide = slideId + util.randomIntFromInterval(1, 10000)
      }
      resolve({
        slideId: `googleSlideId-${idSlide}`,
      })
    }
  })
}

const getAhaSlideIdMap = () => {
  return new Promise((resolve) => {
    if (window.google) {
      window.google.script.run
        .withSuccessHandler((res) => resolve(Array.isArray(res) ? res : []))
        .withFailureHandler((error) => {
          reportAddonFailure(error, 'getAhaSlideIdMap', 'warning')
          resolve([])
        })
        .getAhaSlideIdMap()
    } else {
      resolve([])
    }
  })
}

const setAhaSlideMetadata = (googleSlideId, ahaSlideId) => {
  return new Promise((resolve) => {
    if (window.google) {
      window.google.script.run
        .withSuccessHandler((res) => resolve(res))
        .withFailureHandler((error) => {
          reportAddonFailure(error, 'setAhaSlideMetadata')
          resolve({ ok: false, reason: 'script-failure' })
        })
        .setAhaSlideMetadata(googleSlideId, ahaSlideId)
    } else {
      resolve({ ok: true })
    }
  })
}

// Refreshes the add-in view. Inside the add-in (window.google present) we ask
// Apps Script to re-render the sidebar via google.script.run, which rebuilds the
// HtmlOutput server-side and re-establishes the google.script bridge — a plain
// window.location.reload() would re-fetch the one-time userCodeAppPanel URL
// outside the handshake and break the add-in. Outside the add-in (local dev) the
// page is a normal document, so a window.location.reload() is correct.
const reloadSidebar = () => {
  return new Promise((resolve) => {
    if (window.google) {
      window.google.script.run
        .withSuccessHandler(() => resolve(true))
        .withFailureHandler((error) => {
          reportAddonFailure(error, 'reloadSidebar', 'warning')
          resolve(false)
        })
        .reloadSidebar()
    } else {
      window.location.reload()
      resolve(true)
    }
  })
}

const listenGoogleSlideChangeActive = () => {
  setInterval(async () => {
    if (store.state.isCreatingGoogleSlide) return
    const googleSlideActiveId = await getGoogleSlideActiveId()
    if (!googleSlideActiveId) return
    const index = store.state.presentation.slides.findIndex(
      (slide) => slide.googleSlide && slide.googleSlide.googleSlideId === googleSlideActiveId && slide.type !== 'freestyle',
    )

    if (index !== -1) {
      if (store.state.googleSlideActiveId !== store.state.presentation.slides[index].id) {
        if (store.state.presentation.slides[index].type === 'image') {
          store.state.googleSlideActiveId = null
        } else {
          store.state.googleSlideActiveId = store.state.presentation.slides[index].id
        }
        store.dispatch('setSlideActive', {
          id: null,
          activeTab: 1,
        })
      }
    } else if (store.state.slideActive.id || store.state.googleSlideActiveId) {
      store.state.googleSlideActiveId = null
      store.dispatch('setSlideActive', {
        id: null,
        activeTab: 1,
      })
    }
  }, 300)
}

const processQueue = async () => {
  if (isProcessingQueue) {
    return
  }

  isProcessingQueue = true

  while (updateQueue.length > 0) {
    const { title, resolve, reject } = updateQueue.shift()
    try {
      // eslint-disable-next-line no-await-in-loop, no-use-before-define
      await updateActiveSlideTitle(title)
      resolve()
    } catch (error) {
      reject(error)
    }
  }

  isProcessingQueue = false
}

const enqueueUpdateTitle = (title) => {
  return new Promise((resolve, reject) => {
    updateQueue.push({ title, resolve, reject })
    processQueue()
  })
}

const updateActiveSlideTitle = (title) => {
  return new Promise((resolve) => {
    if (window.google) {
      window.google.script.run
        .withSuccessHandler(async () => {
          resolve()
        })
        // Resolve rather than reject: this is awaited inside processQueue's loop, and a
        // rejection there would leave isProcessingQueue stuck and stall every later sync.
        .withFailureHandler((error) => {
          reportAddonFailure(error, 'updateActiveSlideTitle', 'warning')
          resolve()
        })
        .updateActiveSlideTitle(title)
    } else {
      resolve({
        slideId: `googleSlideId-${slideId}`,
      })
    }
  })
}

const getPresentationName = () => {
  return new Promise((resolve) => {
    if (window.google) {
      window.google.script.run
        .withSuccessHandler(async (name) => {
          resolve(name)
        })
        .withFailureHandler((error) => {
          reportAddonFailure(error, 'getPresentationName', 'warning')
          resolve(null)
        })
        .getPresentationName()
    } else {
      resolve(`presentation-${store.state.currentUser.id}`)
    }
  })
}

const getIndexGoogleSlideActive = async () => {
  const googleSlideActiveId = await getGoogleSlideActiveId()
  if (!googleSlideActiveId) return -1
  return store.state.presentation.slides.findIndex(
    (slide) => slide.googleSlide && slide.googleSlide.googleSlideId === googleSlideActiveId,
  )
}

export default {
  getPresentationGoogleSlide,
  createNewSlideAndSelect,
  setAhaSlideMetadata,
  getAhaSlideIdMap,
  reloadSidebar,
  getGoogleSlideActiveId,
  listenGoogleSlideChangeActive,
  updateActiveSlideTitle,
  enqueueUpdateTitle,
  getPresentationName,
  getIndexGoogleSlideActive,
}
